import React, { ComponentType, useCallback } from "react";
import { Platform } from "react-native";
import { useRoutes as reactRouterUseRoutes } from "react-router";
import { OnI18nErrorFn } from "@lookiero/i18n-react";
import { LookAndLike } from "@lookiero/look-and-like";
import { MessagingRoot } from "@lookiero/messaging-react/bootstrap";
import { Locale } from "@lookiero/sty-psp-locale";
import {
  sentryLoggerHOC,
  sentryLogger,
  SentryEnvironment,
  SentryLoggerFunctionArgs,
} from "@lookiero/sty-psp-logging/sentry";
import { Layout } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Customer } from "../../../projection/customer/customer";
import { KameleoonEnvironment } from "../../ab-testing/kameleoonEnvironment";
import { i18n } from "../i18n/i18n";
import { Routing } from "../routing/Routing";

interface RootComponentProps {
  readonly customer: Customer | undefined;
  readonly layout: Layout;
  readonly locale?: Locale;
  readonly androidInAppBrowser?: boolean;
  readonly lookAndLike: LookAndLike;
  readonly tradename: Tradename;
  readonly onBack: () => void;
  readonly useRoutes?: typeof reactRouterUseRoutes;
}

type RootComponent = ComponentType<RootComponentProps>;

interface RootFunctionArgs {
  readonly Messaging: MessagingRoot;
  readonly development?: boolean;
  readonly sentry: () => SentryEnvironment;
  readonly kameleoon: () => KameleoonEnvironment;
  readonly apiUrl: () => string;
  readonly getAuthToken: () => Promise<string>;
  readonly basePath: string;
}

interface RootFunction {
  (args: RootFunctionArgs): (args: SentryLoggerFunctionArgs) => RootComponent;
}

const root: RootFunction = ({
  Messaging,
  development,
  sentry,
  kameleoon: kameleoonConfig,
  apiUrl,
  getAuthToken,
  basePath,
}) => {
  const logger = sentryLogger(sentry);
  const kameleoon = kameleoonConfig();

  const Root: RootComponent = ({
    customer,
    layout,
    locale = Locale.en_GB,
    lookAndLike,
    androidInAppBrowser = false,
    tradename,
    onBack,
    useRoutes = reactRouterUseRoutes,
  }) => {
    const handleOnI18nError = useCallback<OnI18nErrorFn>(
      (error, fingerprint) =>
        development
          ? void 0
          : logger.captureException(
              error,
              fingerprint ? { captureContext: { fingerprint: [...fingerprint] } } : undefined,
            ),
      [],
    );

    return (
      <Messaging includeReactQueryDevTools={Platform.OS === "web"}>
        <Routing
          I18n={i18n({ tradename })}
          androidInAppBrowser={androidInAppBrowser}
          apiUrl={apiUrl}
          basePath={basePath}
          customer={customer}
          getAuthToken={getAuthToken}
          kameleoon={kameleoon}
          layout={layout}
          locale={locale}
          lookAndLike={lookAndLike}
          tradename={tradename}
          useRoutes={useRoutes}
          onBack={onBack}
          onI18nError={handleOnI18nError}
        />
      </Messaging>
    );
  };

  const hoc = sentryLoggerHOC({ logger });

  /**
   * It's important to not call sentryLoggerHOC each time this function gets called.
   * That's why its called outside this method.
   */
  return ({ customerId }) => hoc({ customerId })(Root);
};

export type { RootComponent };
export { root };
